import { Injectable } from '@angular/core';
import { rethrowAsSapError, SapError } from 'src/app/lego-utils';
import { map } from 'rxjs/operators';
import { TaskListCharacteristic, TaskListOperation } from 'src/app/models/domain';
import { TaskListCharacteristicAdapter } from 'src/app/models/domain/adapters';
import { TextSetService } from 'src/generated-clients/longtext';
import { CharacteristicsSetService } from 'src/generated-clients/tasklist';

@Injectable({
  providedIn: 'root',
})
export class CharacteristicDataService {
  private readonly taskListTypePropertyName: keyof TaskListCharacteristic = 'taskListType';
  private readonly taskListGroupPropertyName: keyof TaskListCharacteristic = 'taskListGroup';
  private readonly taskListGroupCounterPropertyName: keyof TaskListCharacteristic = 'groupCounter';
  private readonly taskListNodesPropertyName: keyof TaskListCharacteristic = 'nodes';

  public constructor(
    private readonly characteristicsSetService: CharacteristicsSetService,
    private readonly characteristicAdapter: TaskListCharacteristicAdapter,
    private readonly textSetService: TextSetService
  ) {}

  public loadByOperation({ taskListGroup, groupCounter, nodes }: TaskListOperation) {
    const taskListTypeFilter = `${this.taskListTypePropertyName} eq 'Q'`;
    const taskListGroupFilter = `${this.taskListGroupPropertyName} eq '${taskListGroup}'`;
    const taskListGroupCounterFilter = `${this.taskListGroupCounterPropertyName} eq '${groupCounter}'`;
    const taskListNodesFilter = `${this.taskListNodesPropertyName} eq '${nodes}'`;

    const filter = `${taskListTypeFilter} and ${taskListGroupFilter} and ${taskListGroupCounterFilter} and ${taskListNodesFilter}`;

    return this.characteristicsSetService.characteristicsSetGet(filter).pipe(
      rethrowAsSapError(),
      map((res) => {
        if (!res.d?.results) {
          throw new SapError('SAP Error');
        }

        return res.d.results.map((characteristic) =>
          this.characteristicAdapter.adaptToDomain(characteristic)
        );
      })
    );
  }

  public getCharacteristicByTaskListHeaders(taskListGroup: string, groupCounters: string[]) {
    const taskListTypeFilter = `${this.taskListTypePropertyName} eq 'Q'`;
    const taskListGroupFilter = `${this.taskListGroupPropertyName} eq '${taskListGroup}'`;

    const groupCounterFilter = groupCounters
      .map((counter) => `${this.taskListGroupCounterPropertyName} eq '${counter}'`)
      .join(' or ');

    const filter = `${taskListTypeFilter} and (${taskListGroupFilter} and (${groupCounterFilter}))`;

    return this.characteristicsSetService.characteristicsSetGet(filter).pipe(
      rethrowAsSapError(),
      map((res) => {
        if (!res.d?.results) {
          throw new SapError('SAP Error');
        }

        return res.d.results.map((characteristic) =>
          this.characteristicAdapter.adaptToDomain(characteristic)
        );
      })
    );
  }

  public getLongText(
    client: string,
    plant: string,
    masterInspectionCharacteristics: string,
    mkVersion: string
  ) {
    const zeroPaddedMkVersion = mkVersion.padStart(6, '0');
    const name = `${client}${plant} ${masterInspectionCharacteristics}${zeroPaddedMkVersion}E`;

    return this.textSetService
      .textSetObjectobjectnamenameididlanguagelanguageGet('QPMERKMAL', name, 'QPMT', 'EN')
      .pipe(
        rethrowAsSapError(),
        map((result) => {
          if (!result.d) {
            throw new SapError('Unknown error');
          }

          return result.d.longText ? result.d.longText.replace(/\|/gu, '\n') : undefined;
        })
      );
  }
}
